import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Track } from '@shared/schema';

interface MusicPlayerContextType {
  currentTrack: Track | null;
  queue: Track[];
  isPlaying: boolean;
  playTrack: (track: Track) => void;
  pauseTrack: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  addToQueue: (track: Track) => void;
  removeFromQueue: (trackId: number) => void;
  clearQueue: () => void;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | undefined>(undefined);

export const MusicPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [queue, setQueue] = useState<Track[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [history, setHistory] = useState<Track[]>([]);

  const playTrack = useCallback((track: Track) => {
    // If it's the same track, just resume playing
    if (currentTrack && currentTrack.id === track.id) {
      setIsPlaying(true);
      return;
    }

    // If we have a current track, add it to history before switching
    if (currentTrack) {
      setHistory(prev => [...prev, currentTrack]);
    }

    setCurrentTrack(track);
    setIsPlaying(true);

    // Remove the track from queue if it's there
    if (queue.some(t => t.id === track.id)) {
      setQueue(queue.filter(t => t.id !== track.id));
    }
  }, [currentTrack, queue]);

  const pauseTrack = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const nextTrack = useCallback(() => {
    if (queue.length === 0) return;
    
    // If we have a current track, add it to history
    if (currentTrack) {
      setHistory(prev => [...prev, currentTrack]);
    }
    
    // Play the next track in queue
    const nextTrack = queue[0];
    const newQueue = queue.slice(1);
    
    setCurrentTrack(nextTrack);
    setQueue(newQueue);
    setIsPlaying(true);
  }, [currentTrack, queue]);

  const previousTrack = useCallback(() => {
    if (history.length === 0) return;
    
    // Get the last track from history
    const previousTrack = history[history.length - 1];
    const newHistory = history.slice(0, history.length - 1);
    
    // Add current track to the beginning of the queue
    if (currentTrack) {
      setQueue(prev => [currentTrack, ...prev]);
    }
    
    setCurrentTrack(previousTrack);
    setHistory(newHistory);
    setIsPlaying(true);
  }, [currentTrack, history]);

  const addToQueue = useCallback((track: Track) => {
    setQueue(prev => [...prev, track]);
  }, []);

  const removeFromQueue = useCallback((trackId: number) => {
    setQueue(prev => prev.filter(track => track.id !== trackId));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  // Reset player when unmounting
  useEffect(() => {
    return () => {
      setCurrentTrack(null);
      setQueue([]);
      setIsPlaying(false);
      setHistory([]);
    };
  }, []);

  const value = {
    currentTrack,
    queue,
    isPlaying,
    playTrack,
    pauseTrack,
    nextTrack,
    previousTrack,
    addToQueue,
    removeFromQueue,
    clearQueue
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (context === undefined) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
};
